import React, { useEffect, useState } from 'react'
import { Button, Modal, Form, Input, Upload, message, Popconfirm, Tag, Row, Col, Empty } from 'antd'
import { PlusOutlined, EditOutlined, UploadOutlined, DownloadOutlined, InboxOutlined, FileOutlined } from '@ant-design/icons'
import api from '../../api'
import { useAuth } from '../../store/auth'

const { Dragger } = Upload

export default function AssetsPage() {
  const { user } = useAuth()
  const [assets, setAssets] = useState<any[]>([])
  const [uploadModal, setUploadModal] = useState(false)
  const [uploadFiles, setUploadFiles] = useState<any[]>([])
  const [uploadForm] = Form.useForm()
  const [uploading, setUploading] = useState(false)
  const [uploadingCount, setUploadingCount] = useState(0)
  const [editAsset, setEditAsset] = useState<any>(null)
  const [editAssetForm] = Form.useForm()

  const loadAssets = () => api.get('/help/assets').then((r: any) => setAssets(r)).catch(() => {})

  useEffect(() => { loadAssets() }, [])

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin'

  const handleUpload = async () => {
    if (uploadFiles.length === 0) { message.warning('请选择文件'); return }
    setUploading(true)
    const values = uploadForm.getFieldsValue()
    try {
      for (const file of uploadFiles) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('name', uploadFiles.length === 1 ? (values.name?.trim() || '') : '')
        formData.append('description', uploadFiles.length === 1 ? (values.description?.trim() || '') : '')
        await api.post('/help/assets', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      }
      message.success('上传成功')
      setUploadModal(false)
      setUploadFiles([])
      uploadForm.resetFields()
      loadAssets()
    } catch { message.error('上传失败') }
    finally { setUploading(false) }
  }

  const handleEditAsset = async () => {
    const values = editAssetForm.getFieldsValue()
    await api.patch(`/help/assets/${editAsset.id}`, { name: values.name?.trim(), description: values.description?.trim() || '' })
    message.success('已更新')
    setEditAsset(null)
    loadAssets()
  }

  const handleDeleteAsset = async (id: number) => {
    await api.delete(`/help/assets/${id}`)
    message.success('已删除')
    setEditAsset(null)
    loadAssets()
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#4A4A4A' }}>常用素材和字体</div>
          <div style={{ color: '#B9B9B9', fontSize: 13, marginTop: 4 }}>PSD、字体包、压缩包等常用素材文件</div>
        </div>
        {isAdmin && (
          <Button icon={<UploadOutlined />} type="primary" size="small"
            onClick={() => { setUploadModal(true); uploadForm.resetFields(); setUploadFiles([]) }}>上传文件</Button>
        )}
      </div>

      {/* 拖拽上传区域 */}
      {isAdmin && (
        <div style={{ marginBottom: 20 }}>
          <Dragger
            multiple
            showUploadList={false}
            disabled={uploadingCount > 0}
            customRequest={async ({ file, onSuccess, onError }) => {
              setUploadingCount(c => c + 1)
              try {
                const formData = new FormData()
                formData.append('file', file as any)
                formData.append('name', '')
                formData.append('description', '')
                await api.post('/help/assets', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
                onSuccess?.({})
                message.success(`${(file as any).name} 上传成功`)
                loadAssets()
              } catch {
                onError?.(new Error('上传失败'))
                message.error(`${(file as any).name} 上传失败`)
              } finally {
                setUploadingCount(c => c - 1)
              }
            }}
            style={{ background: '#FAFBFF', borderColor: '#D8DEFC', borderRadius: 12 }}
          >
            {uploadingCount > 0 ? (
              <>
                <p style={{ color: '#536DFE', fontSize: 32 }}><InboxOutlined /></p>
                <p style={{ color: '#536DFE', fontSize: 14, fontWeight: 500 }}>上传中...（{uploadingCount} 个文件）</p>
              </>
            ) : (
              <>
                <p style={{ color: '#536DFE', fontSize: 32 }}><InboxOutlined /></p>
                <p style={{ color: '#4A4A4A', fontSize: 14 }}>点击或拖拽文件到此区域直接上传</p>
                <p style={{ color: '#B9B9B9', fontSize: 12 }}>支持 PSD、ZIP、RAR、字体文件等</p>
              </>
            )}
          </Dragger>
        </div>
      )}

      {/* 文件列表 - 卡片布局 */}
      {assets.length === 0 ? (
        <Empty description="暂无素材文件" style={{ paddingTop: 60 }} />
      ) : (
        <Row gutter={[16, 16]}>
          {assets.map((row: any) => (
            <Col key={row.id} xs={12} sm={8} md={6} lg={4}>
              <div style={{
                background: '#fff', borderRadius: 12, overflow: 'hidden',
                boxShadow: '0 2px 8px #E8EAFC', display: 'flex', flexDirection: 'column',
              }}>
                <div style={{
                  width: '100%', height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#F6F7FF', borderBottom: '1px solid #f0f0f0',
                }}>
                  {row.thumbnail ? (
                    <img src={`/api/help/assets/thumbnail/${row.id}`} alt={row.name}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <FileOutlined style={{ fontSize: 48, color: '#D8DEFC' }} />
                  )}
                </div>
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</div>
                  {row.description && <div style={{ fontSize: 11, color: '#999', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description}</div>}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: '#aaa' }}>
                    <span><Tag style={{ fontSize: 11, lineHeight: '18px', margin: 0 }}>{row.uploader}</Tag> {formatSize(row.file_size)}</span>
                    <span>{row.created_at?.slice(5, 10)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                    <a href={`/api/help/assets/download/${row.id}`} download
                      style={{ fontSize: 12, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <DownloadOutlined /> 下载
                    </a>
                    {isAdmin && <EditOutlined style={{ fontSize: 13, color: '#999', cursor: 'pointer' }}
                      onClick={() => { setEditAsset(row); editAssetForm.setFieldsValue({ name: row.name, description: row.description }) }} />}
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {/* 上传弹窗（带名称说明） */}
      <Modal title="上传素材文件" open={uploadModal} onCancel={() => setUploadModal(false)}
        confirmLoading={uploading} onOk={handleUpload} destroyOnClose>
        <Form form={uploadForm} layout="vertical">
          <Form.Item label="选择文件" required>
            <Upload multiple beforeUpload={(file) => { setUploadFiles(prev => [...prev, file]); return false }}
              fileList={uploadFiles} onRemove={(file) => setUploadFiles(prev => prev.filter(f => f.uid !== file.uid))}>
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="name" label="文件名称（可选，留空使用原文件名）">
            <Input placeholder="如：常用字体包" />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input placeholder="如：包含思源黑体、阿里巴巴普惠体等" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑素材 */}
      <Modal title="编辑素材" open={!!editAsset} onCancel={() => setEditAsset(null)} onOk={handleEditAsset} destroyOnClose
        footer={(_, { OkBtn, CancelBtn }) => (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Popconfirm title="确定删除该文件？" onConfirm={() => handleDeleteAsset(editAsset?.id)} okText="删除" cancelText="取消">
              <Button danger>删除文件</Button>
            </Popconfirm>
            <div style={{ display: 'flex', gap: 8 }}>
              <CancelBtn />
              <OkBtn />
            </div>
          </div>
        )}>
        <Form form={editAssetForm} layout="vertical">
          <Form.Item name="name" label="文件名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="说明"><Input placeholder="文件用途说明" /></Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
